import React, { useState } from "react";
import PropTypes from "prop-types";
import Header from "components/order-profile/header";
import VerifyOrderProductCompatibility from "./verify-order-product-compatibility";
import { selectors } from "reducers";
import { useSelector } from "react-redux";
import ModalLink from "components/shared/modal-link";
import { InlineWaiting } from "components/ui";
import {
  QuantityInput,
  ZeroBilledInput,
  DropshipInput,
  ClaimNoteInput
} from "./inputs";
import { MdContentCopy } from "react-icons/lib/md";
import { IoClose } from "react-icons/lib/io";
import { OrderProfileContext } from "../context";
import { useFetch } from "hooks";
import { del } from "utils/api";

const OrderLineItems = ({ orderId }) => {
  const { fetchOrderLineItemsApi, order, updateOrderLineItemByIdApi } =
    React.useContext(OrderProfileContext);

  const orderLineItems = fetchOrderLineItemsApi.response;

  const userRole = useSelector(selectors.getUserRole);

  const deleteOrderLineItemApi = useFetch({
    apiFn: line_item_id => del(`orders/line_item/${line_item_id}`),
    onSuccess: () => fetchOrderLineItemsApi.fetchData(orderId)
  });
  const [animate, setAnimate] = useState({
    animate: false,
    index: 0
  });

  const CopyToClipboard = value => {
    navigator.clipboard.writeText(value);
  };

  const canRemoveLineItem = ![
    "COMPLETED",
    "PENDING SHIPMENT",
    "REJECTED"
  ].includes(order.order_status?.toUpperCase());

  /* coaches can only update new and coach follow up orders */
  /* everyone else can update all orders except completed, accepted, billed, voided, and rejected */
  const canChangeQuantities = [
    "Administrator",
    "CompanyAdministrator",
    "OfficeAdministrator",
    "ServiceAdmin",
    "ServiceCoach",
    "ContractorAdministrator"
  ].includes(userRole)
    ? !["COMPLETED", "ACCEPTED", "BILLED", "VOIDED", "REJECTED"].includes(
        order.order_status?.toUpperCase()
      )
    : ["NEW", "COACH FOLLOW UP"].includes(order.order_status?.toUpperCase());

  const hasLineWithProductType = equipmentType => {
    return orderLineItems?.some(x =>
      x.product_type?.toLowerCase().includes(equipmentType?.toLowerCase())
    );
  };

  const uses_bonafide_api = order.uses_bonafide_api;

  return (
    <div>
      <Header showSpinner={fetchOrderLineItemsApi.isFetching}>
        Equipment Requested:{" "}
        {order.equipment_requested?.map(x => (
          <span
            className={`equipment-label ${hasLineWithProductType(x) ? "hasLineItem" : ""}`}
            key={x}
          >
            {x}
          </span>
        ))}
      </Header>
      <table style={{ width: "100%" }}>
        <thead>
          <tr>
            <th />
            <th />
            <th>Type</th>
            <th>Product ID</th>
            <th>Manufacturer</th>
            <th>HCPCS</th>
            <th>Product</th>
            <th>Qty</th>
            <th>Zero Billed</th>
            {uses_bonafide_api ? <th>Dropship</th> : ""}
            {uses_bonafide_api ? <th>Claim Note</th> : ""}
          </tr>
        </thead>
        <tbody>
          {orderLineItems.map((x, idx) => (
            <tr
              key={x.line_item_id}
              className={
                order.flag_generic_items && x.item_name?.includes("Generic")
                  ? "generic-item-flag"
                  : ""
              }
            >
              <td style={{ width: 15 }}>
                {canRemoveLineItem && (
                  <>
                    {deleteOrderLineItemApi.isFetching ? (
                      <InlineWaiting />
                    ) : (
                      <i
                        onClick={() => {
                          deleteOrderLineItemApi.fetchData(x.line_item_id);
                        }}
                        className="remove-equipment-x"
                      >
                        <IoClose />
                      </i>
                    )}
                  </>
                )}
              </td>
              <td>
                <VerifyOrderProductCompatibility {...x} />
              </td>
              <td>{x.product_type}</td>
              <td
                title={`${x.company_item_id ? "Manufacturer Id" : "Crosswalk Id"}`}
                style={{ display: "flex", alignItems: "center", minHeight: 90 }}
              >
                {x.company_item_id ? x.company_item_id : x.manufacturer_id}
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "20px",
                    marginLeft: 10,
                    color: "grey"
                  }}
                  className={`${
                    animate.animate && animate.index === idx
                      ? "copied-animation"
                      : ""
                  }`}
                  title="Copy To Clipboard"
                  onAnimationEnd={() =>
                    setAnimate({ animate: false, index: 0 })
                  }
                >
                  <MdContentCopy
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      CopyToClipboard(
                        x.company_item_id
                          ? x.company_item_id
                          : x.manufacturer_id
                      );
                      setAnimate({ animate: true, index: idx });
                    }}
                  />
                </div>
              </td>
              <td>{x.manufacturer}</td>
              <td>{x.hcpc}</td>
              <td>
                <ModalLink
                  to={`/companies/${x.company_id}/profile/inventory/${x.company_inventory_guid}`}
                >
                  {x.item_name}
                </ModalLink>
              </td>
              <td>
                <QuantityInput
                  orderId={orderId}
                  line_item_id={x.line_item_id}
                  quantity={x.quantity}
                  updateOrderLineItemByIdApi={updateOrderLineItemByIdApi}
                  canChangeQuantities={canChangeQuantities}
                />
              </td>
              <td>
                <ZeroBilledInput
                  orderId={orderId}
                  line_item_id={x.line_item_id}
                  value={x.zero_billed}
                  updateOrderLineItemByIdApi={updateOrderLineItemByIdApi}
                  canChangeQuantities={canChangeQuantities}
                />
              </td>
              {uses_bonafide_api && (
                <td>
                  <DropshipInput
                    canChangeQuantities={canChangeQuantities}
                    orderId={orderId}
                    line_item_id={x.line_item_id}
                    value={x.dropship_item}
                    updateOrderLineItemByIdApi={updateOrderLineItemByIdApi}
                  />
                </td>
              )}
              {uses_bonafide_api && (
                <td>
                  <ClaimNoteInput
                    canChangeQuantities={canChangeQuantities}
                    orderId={orderId}
                    line_item_id={x.line_item_id}
                    value={x.claim_order_note}
                    updateOrderLineItemByIdApi={updateOrderLineItemByIdApi}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OrderLineItems;

OrderLineItems.propTypes = {
  orderId: PropTypes.string.isRequired
};
